import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.affiliate_code || '—'})`;
}

function currentPeriod() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function StatusBadge({ status, isZh }) {
  const styles = {
    released: { backgroundColor: 'rgba(201,169,97,0.2)', color: '#c9a961', label: isZh ? '已发放' : 'Released' },
    pending: { backgroundColor: 'rgba(201,169,97,0.05)', color: '#b8a68f', label: isZh ? '待发放' : 'Pending' },
  };
  const s = styles[status] || styles.pending;
  return (
    <span className="px-2 py-1 rounded text-body-xs font-medium" style={{ backgroundColor: s.backgroundColor, color: s.color }}>
      {s.label}
    </span>
  );
}

function LegCell({ label, legPosition, value, isWeak, onExpand, expanded, breakdown, loading, isZh }) {
  return (
    <div>
      <button
        type="button"
        onClick={() => onExpand(legPosition)}
        className="text-left underline decoration-dotted"
        style={{ color: isWeak ? '#d97706' : '#c9a961', fontWeight: isWeak ? 700 : 400 }}
        title={`${label}${isWeak ? (isZh ? '（弱腿 — 已匹配）' : ' (weak leg — matched)') : ''}`}
      >
        {label}: {Number(value).toFixed(2)}
      </button>
      {expanded && (
        <div className="mt-2 p-2 rounded text-body-xs" style={{ backgroundColor: '#152214', border: '1px solid #3d5a4a', color: '#b8a68f' }}>
          <p className="mb-1" style={{ color: '#999999' }}>{isZh ? '当前族谱' : 'Current genealogy'}{breakdown?.snapshot_total !== null && breakdown?.snapshot_total !== undefined ? (isZh ? `（计算快照：${Number(breakdown.snapshot_total).toFixed(2)}）` : ` (calculated snapshot: ${Number(breakdown.snapshot_total).toFixed(2)})`) : ''}</p>
          {loading ? (
            <p>{isZh ? '加载中...' : 'Loading...'}</p>
          ) : breakdown && breakdown.purchases.length > 0 ? (
            <>
              <p className="mb-1">{isZh ? '总计' : 'Total'}: {Number(breakdown.current_total).toFixed(2)}</p>
              <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                {breakdown.purchases.map((p, i) => (
                  <li key={i}>
                    {memberLabel(p.buyer)} — {p.package_code} ({Number(p.package_price).toFixed(2)})
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p>{isZh ? '此腿下暂无已确认的购买记录。' : 'No confirmed purchases under this leg.'}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function TeamBonusOld() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const [period, setPeriod] = useState(currentPeriod());
  const [run, setRun] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [excludingId, setExcludingId] = useState(null);

  const [exclusions, setExclusions] = useState([]);
  const [exclusionsLoading, setExclusionsLoading] = useState(true);

  const [expandedLeg, setExpandedLeg] = useState(null); // `${recordId}-${legPosition}`
  const [breakdowns, setBreakdowns] = useState({});
  const [breakdownLoading, setBreakdownLoading] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  const fetchList = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/team-bonus/list?year=${period.year}&month=${period.month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load team bonus');
      }
      setRun(data.run);
      setRecords(data.records || []);
      setSelectedIds({});
    } catch (err) {
      setError(err.message || 'Failed to load team bonus');
    } finally {
      setLoading(false);
    }
  };

  const fetchExclusions = async () => {
    setExclusionsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/team-bonus/exclusions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load exclusions');
      }
      setExclusions(data.exclusions || []);
    } catch (err) {
      setError(err.message || 'Failed to load exclusions');
    } finally {
      setExclusionsLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== 'superadmin') return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, period.year, period.month]);

  useEffect(() => {
    if (!user || user.role !== 'superadmin') return;
    fetchExclusions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const handleCalculate = async () => {
    setCalculating(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/team-bonus/calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ period_year: period.year, period_month: period.month }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to calculate team bonus');
      }
      setStatusMessage(tt(`${data.records_created} member(s) matched, total ${Number(data.total_bonus).toFixed(2)} CP.`, `已匹配 ${data.records_created} 位会员，共计 ${Number(data.total_bonus).toFixed(2)} CP。`));
      await fetchList();
    } catch (err) {
      setError(err.message || tt('Failed to calculate team bonus', '计算团队奖金(对碰)失败'));
    } finally {
      setCalculating(false);
    }
  };

  const handleRelease = async (ids) => {
    if (!ids || ids.length === 0) return;
    setReleasing(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/team-bonus/release`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ record_ids: ids }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to release team bonus');
      }
      const failures = (data.results || []).filter((r) => !r.success);
      const successes = (data.results || []).filter((r) => r.success);
      if (failures.length > 0) {
        setStatusMessage(isZh
          ? `已发放 ${successes.length} 条，${failures.length} 条失败：${failures.map((f) => f.message).join('; ')}`
          : `Released ${successes.length}, ${failures.length} failed: ${failures.map((f) => f.message).join('; ')}`);
      } else {
        setStatusMessage(tt(`Released ${successes.length} record${successes.length === 1 ? '' : 's'}.`, `已发放 ${successes.length} 条记录。`));
      }
      await fetchList();
    } catch (err) {
      setError(err.message || tt('Failed to release team bonus', '发放团队奖金(对碰)失败'));
    } finally {
      setReleasing(false);
    }
  };

  const handleExclude = async (recordId) => {
    const reason = window.prompt(tt('Reason for excluding this member from future Team Bonus calculations (optional):', '将此会员从未来的团队奖金(对碰)计算中排除的原因（可选）：'), '');
    if (reason === null) return;
    setExcludingId(recordId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/team-bonus/exclude`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ record_id: recordId, reason: reason || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to exclude member');
      }
      setStatusMessage(data.message);
      await fetchList();
      await fetchExclusions();
    } catch (err) {
      setError(err.message || 'Failed to exclude member');
    } finally {
      setExcludingId(null);
    }
  };

  const handleRemoveExclusion = async (exclusionId) => {
    setExcludingId(exclusionId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/team-bonus/remove-exclusion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ exclusion_id: exclusionId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to remove exclusion');
      }
      setStatusMessage(data.message);
      await fetchExclusions();
    } catch (err) {
      setError(err.message || 'Failed to remove exclusion');
    } finally {
      setExcludingId(null);
    }
  };

  const handleExpandLeg = async (record, legPosition) => {
    const key = `${record.id}-${legPosition}`;
    if (expandedLeg === key) {
      setExpandedLeg(null);
      return;
    }
    setExpandedLeg(key);
    if (!breakdowns[key]) {
      setBreakdownLoading(key);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/admin/team-bonus/leg-breakdown?user_id=${record.recipient_user_id}&leg_position=${legPosition}&record_id=${record.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await res.json();
        if (res.ok && data.success) {
          setBreakdowns((prev) => ({ ...prev, [key]: data }));
        }
      } catch (err) {
        // swallow -- breakdown is a drill-down convenience, not critical path
      } finally {
        setBreakdownLoading(null);
      }
    }
  };

  const pendingRecords = useMemo(() => records.filter((r) => r.status === 'pending'), [records]);
  const releasedRecords = useMemo(() => records.filter((r) => r.status === 'released'), [records]);
  const tabRecords = activeTab === 'pending' ? pendingRecords : activeTab === 'released' ? releasedRecords : [];

  const allPendingSelected = pendingRecords.length > 0 && pendingRecords.every((r) => selectedIds[r.id]);

  const toggleSelectAll = () => {
    if (allPendingSelected) {
      setSelectedIds({});
    } else {
      const next = {};
      pendingRecords.forEach((r) => { next[r.id] = true; });
      setSelectedIds(next);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;

  if (!user || user.role !== 'superadmin') {
    return null;
  }

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-headline-lg font-bold" style={{ color: '#c9a961' }}>
            {tt('Team Bonus', '团队奖金(对碰)')}
          </h1>
          <div className="flex items-center gap-3">
            <input
              type="month"
              value={`${period.year}-${String(period.month).padStart(2, '0')}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map((v) => parseInt(v, 10));
                if (y && m) setPeriod({ year: y, month: m });
              }}
              className="px-3 py-2 rounded-lg border text-body-sm"
              style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#c9a961' }}
            />
            <button
              type="button"
              onClick={handleCalculate}
              disabled={calculating}
              className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
              style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
            >
              {calculating ? tt('Calculating...', '计算中...') : tt('Calculate', '计算')}
            </button>
          </div>
        </div>

        {error && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
            {error}
          </div>
        )}
        {statusMessage && (
          <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#b8a68f' }}>
            {statusMessage}
          </div>
        )}

        <div className="border rounded-lg p-4 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
          {run ? (
            <p className="text-body-sm" style={{ color: '#b8a68f' }}>
              {tt(`${run.members_paid} member${run.members_paid === 1 ? '' : 's'} matched`, `已匹配 ${run.members_paid} 位会员`)}
              {' · '}{tt('Total', '总计')}: <span style={{ color: '#c9a961' }}>{Number(run.total_bonus_paid).toFixed(2)} CP</span>
            </p>
          ) : (
            <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('Not yet calculated for this batch label.', '本批次标签尚未计算。')}</p>
          )}
        </div>

        <div className="flex gap-2 mb-4 border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
          <button
            type="button"
            onClick={() => setActiveTab('pending')}
            className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
            style={activeTab === 'pending' ? { color: '#0d2818', backgroundColor: '#c9a961' } : { color: '#c9a961', backgroundColor: 'transparent' }}
          >
            {tt('Pending', '待发放')} ({pendingRecords.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('released')}
            className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
            style={activeTab === 'released' ? { color: '#0d2818', backgroundColor: '#c9a961' } : { color: '#c9a961', backgroundColor: 'transparent' }}
          >
            {tt('Released', '已发放')} ({releasedRecords.length})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('exclusions')}
            className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
            style={activeTab === 'exclusions' ? { color: '#0d2818', backgroundColor: '#c9a961' } : { color: '#c9a961', backgroundColor: 'transparent' }}
          >
            {tt('Exclusions', '排除列表')} ({exclusions.length})
          </button>
        </div>

        {activeTab === 'exclusions' ? (
          exclusionsLoading ? (
            <p style={{ color: '#b8a68f' }}>{tt('Loading exclusions...', '正在加载排除列表...')}</p>
          ) : exclusions.length === 0 ? (
            <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <p style={{ color: '#b8a68f' }}>{tt('No excluded members. Use the "Exclude" button on a pending record to stop that member from being recalculated in the future.', '暂无已排除的会员。可在待发放记录上点击"排除"按钮，使该会员不再被重新计算。')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                <thead>
                  <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                    <th className="p-2">{tt('Member', '会员')}</th>
                    <th className="p-2">{tt('Reason', '原因')}</th>
                    <th className="p-2">{tt('Excluded By', '操作人')}</th>
                    <th className="p-2">{tt('Date', '日期')}</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {exclusions.map((ex) => (
                    <tr key={ex.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                      <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(ex.member)}</td>
                      <td className="p-2">{ex.reason || '—'}</td>
                      <td className="p-2">{memberLabel(ex.excluded_by)}</td>
                      <td className="p-2">{new Date(ex.created_at).toLocaleDateString()}</td>
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => handleRemoveExclusion(ex.id)}
                          disabled={excludingId === ex.id}
                          className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                          style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                        >
                          {excludingId === ex.id ? tt('Removing...', '移除中...') : tt('Re-include', '重新纳入')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <>
            {activeTab === 'pending' && pendingRecords.length > 0 && (
              <div className="flex items-center gap-3 mb-4">
                <label className="flex items-center gap-2 text-body-sm" style={{ color: '#b8a68f' }}>
                  <input type="checkbox" checked={allPendingSelected} onChange={toggleSelectAll} />
                  {tt('Select all pending', '全选待发放')}
                </label>
                <button
                  type="button"
                  onClick={() => handleRelease(Object.keys(selectedIds).filter((id) => selectedIds[id]))}
                  disabled={releasing || selectedCount === 0}
                  className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                  style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                >
                  {releasing ? tt('Releasing...', '发放中...') : tt(`Release Selected (${selectedCount})`, `发放所选 (${selectedCount})`)}
                </button>
              </div>
            )}

            {loading ? (
              <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
            ) : records.length === 0 ? (
              <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <p style={{ color: '#b8a68f' }}>{tt('No records yet. Click Calculate to compute Team Bonus matches.', '暂无记录。点击"计算"以计算团队奖金(对碰)匹配结果。')}</p>
              </div>
            ) : tabRecords.length === 0 ? (
              <div className="border rounded-lg p-6 text-center" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <p style={{ color: '#b8a68f' }}>{tt(`No ${activeTab} records.`, `暂无${activeTab === 'pending' ? '待发放' : '已发放'}记录。`)}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                  <thead>
                    <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                      <th className="p-2"></th>
                      <th className="p-2">{tt('Recipient', '接收人')}</th>
                      <th className="p-2">{tt('Legs (1 / 2 / 3)', '腿（1 / 2 / 3）')}</th>
                      <th className="p-2">{tt('Matched Volume', '匹配业绩')}</th>
                      <th className="p-2">{tt('Cash Points', '现金积分')}</th>
                      <th className="p-2">{tt('Product Points', '产品积分')}</th>
                      <th className="p-2">{tt('Status', '状态')}</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabRecords.map((r) => (
                      <tr key={r.id} className="border-b align-top" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                        <td className="p-2">
                          {r.status === 'pending' && (
                            <input
                              type="checkbox"
                              checked={!!selectedIds[r.id]}
                              onChange={() => toggleSelect(r.id)}
                            />
                          )}
                        </td>
                        <td className="p-2" style={{ color: '#c9a961' }}>
                          {r.recipient_ranking_snapshot ? '★'.repeat(r.recipient_ranking_snapshot) + ' ' : ''}{memberLabel(r.recipient)}
                        </td>
                        <td className="p-2 space-y-1">
                          {[1, 2, 3].map((legPos) => {
                            const value = legPos === 1 ? r.leg1_volume_snapshot : legPos === 2 ? r.leg2_volume_snapshot : r.leg3_volume_snapshot;
                            const key = `${r.id}-${legPos}`;
                            return (
                              <LegCell
                                key={legPos}
                                label={tt(`Leg ${legPos}`, `第 ${legPos} 腿`)}
                                legPosition={legPos}
                                value={value}
                                isWeak={r.weak_leg_position === legPos}
                                onExpand={() => handleExpandLeg(r, legPos)}
                                expanded={expandedLeg === key}
                                breakdown={breakdowns[key]}
                                loading={breakdownLoading === key}
                                isZh={isZh}
                              />
                            );
                          })}
                        </td>
                        <td className="p-2">{Number(r.matched_amount).toFixed(2)}</td>
                        <td className="p-2">{Number(r.cash_amount).toFixed(2)}</td>
                        <td className="p-2">{Number(r.credit_amount).toFixed(2)}</td>
                        <td className="p-2"><StatusBadge status={r.status} isZh={isZh} /></td>
                        <td className="p-2">
                          <div className="flex flex-col gap-2">
                            {r.status === 'pending' && (
                              <button
                                type="button"
                                onClick={() => handleRelease([r.id])}
                                disabled={releasing}
                                className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                                style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                              >
                                {tt('Release', '发放')}
                              </button>
                            )}
                            {r.status === 'pending' && (
                              <button
                                type="button"
                                onClick={() => handleExclude(r.id)}
                                disabled={excludingId === r.id}
                                title={tt('Exclude this member from all future Team Bonus calculations', '将此会员从未来所有团队奖金(对碰)计算中排除')}
                                className="px-3 py-1 rounded font-medium text-body-xs border disabled:opacity-50"
                                style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                              >
                                {excludingId === r.id ? tt('Excluding...', '排除中...') : tt('Exclude', '排除')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
